import React, { useState } from 'react';
import { Bid, ProposalData, CompanyInfo, ClientInfo } from '../../types/bidding';
import { generateProposalPDF, downloadProposalPDF } from '../../utils/proposalTemplate';
import { uploadBidProposal } from '../../utils/firebase';
import { useCRM } from '../../utils/CRMContext';
import { formatCurrency } from '../../utils/bidCalculator';
import BidGroupEditor from './BidGroupEditor';
import './BidProposalEditor.css';

interface BidProposalEditorProps {
  bid: Bid;
}

const BidProposalEditor: React.FC<BidProposalEditorProps> = ({ bid }) => {
  const { updateBid } = useCRM();
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState<'edit' | 'preview'>('preview');

  const handleGeneratePDF = async () => {
    setIsGenerating(true);
    
    try {
      // Prepare proposal data
      const proposalData: ProposalData = {
        bid,
        companyInfo: {
          name: "Leiper's Creek Timber Frames",
          address: '3745 Robinson Rd., Thompsons Station, TN 37179',
          phone: '(555) 123-4567', // TODO: Get from settings
          email: 'info@lctimberframes.com',
        },
        clientInfo: {
          name: bid.projectName,
          email: '', // TODO: Get from linked lead/deal
        },
        generatedAt: new Date(),
      };

      // Generate PDF
      const pdfBlob = await generateProposalPDF(proposalData);
      
      // Upload to Firebase Storage
      const proposalUrl = await uploadBidProposal(bid.id, bid.bidNumber, bid.version, pdfBlob);
      
      // Update bid with proposal URL
      await updateBid(bid.id, { proposalUrl });
      
      // Download PDF
      downloadProposalPDF(pdfBlob, `${bid.bidNumber}.pdf`);
      
      alert('Proposal generated and saved successfully!');
    } catch (error) {
      console.error('Error generating proposal:', error);
      alert('Failed to generate proposal');
    } finally {
      setIsGenerating(false);
    }
  };

  const grandTotal = bid.groups.reduce((sum, group) => sum + group.total, 0);

  return (
    <div className="bid-proposal-editor">
      <div className="proposal-header">
        <h3>Proposal</h3>
        <div className="proposal-actions">
          <button
            className={`mode-btn ${previewMode === 'preview' ? 'active' : ''}`}
            onClick={() => setPreviewMode('preview')}
          >
            Preview
          </button>
          <button
            className={`mode-btn ${previewMode === 'edit' ? 'active' : ''}`}
            onClick={() => setPreviewMode('edit')}
          >
            Edit
          </button>
          <button
            className="generate-btn"
            onClick={handleGeneratePDF}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate PDF'}
          </button>
        </div>
      </div>

      {previewMode === 'preview' && (
        <div className="proposal-preview">
          <div className="proposal-header-section">
            <h1>{bid.projectName}</h1>
            <div className="proposal-meta">
              <span>Bid #: {bid.bidNumber}</span>
              <span>•</span>
              <span>Date: {new Date().toLocaleDateString()}</span>
            </div>
          </div>

          <div className="proposal-summary-section">
            <div className="summary-card">
              <h2>Investment Summary</h2>
              <div className="summary-total">
                {formatCurrency(grandTotal)}
              </div>
            </div>
          </div>

          {bid.groups.map((group, index) => (
            <div key={group.id} className="proposal-group-section">
              <h3 className="group-title">{group.name}</h3>
              
              <div className="bucket-summary">
                {group.designBucket.total > 0 && (
                  <div className="bucket-row">
                    <span>Design:</span>
                    <span>{formatCurrency(group.designBucket.total)}</span>
                  </div>
                )}
                {group.productionBucket.total > 0 && (
                  <div className="bucket-row">
                    <span>Production ({group.productionBucket.totalBoardFeet.toFixed(2)} BF):</span>
                    <span>{formatCurrency(group.productionBucket.total)}</span>
                  </div>
                )}
                {group.freightBucket.total > 0 && (
                  <div className="bucket-row">
                    <span>Freight:</span>
                    <span>{formatCurrency(group.freightBucket.total)}</span>
                  </div>
                )}
                {group.installBucket.total > 0 && (
                  <div className="bucket-row">
                    <span>Installation:</span>
                    <span>{formatCurrency(group.installBucket.total)}</span>
                  </div>
                )}
                {group.materialsBucket.total > 0 && (
                  <div className="bucket-row">
                    <span>Materials:</span>
                    <span>{formatCurrency(group.materialsBucket.total)}</span>
                  </div>
                )}
              </div>

              <div className="group-totals">
                {/* Note: Profit margin is hidden from client-facing proposals */}
                <div className="group-total-row">
                  <span><strong>{group.name} Total:</strong></span>
                  <span><strong>{formatCurrency(group.total)}</strong></span>
                </div>
              </div>
            </div>
          ))}

          <div className="proposal-footer">
            <div className="terms-section">
              <h3>Terms & Conditions</h3>
              <ul>
                <li>Payment: 50% deposit required upon acceptance, balance due upon completion.</li>
                <li>Timeline: Work will commence within 2 weeks of deposit receipt.</li>
                <li>Warranty: All timber materials and workmanship are warranted for 1 year.</li>
                <li>Validity: This proposal is valid for 30 days from the date above.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {previewMode === 'edit' && (
        <div className="proposal-edit">
          <p className="edit-note">
            Make changes to the bid structure below. Changes will be reflected in the generated proposal.
          </p>
          <BidGroupEditor
            groups={bid.groups}
            onUpdate={(groups) => updateBid(bid.id, { groups })}
            readOnly={false}
          />
        </div>
      )}
    </div>
  );
};

export default BidProposalEditor;
